// src/pages/Admin/ProgramCategoriesCreate.js
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';
import { ProgressBar } from 'react-bootstrap';
import { downloadBulkUploadTemplate } from '../../utils/bulkUploadTemplates';

function ProgramCategoriesCreate() {
  const navigate = useNavigate();
  
  // Single creation states
  const [categoryName, setCategoryName] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [description, setDescription] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  
  // Bulk upload state
  const [uploadFile, setUploadFile] = useState(null);
  
  // Feedback message
  const [tooltip, setTooltip] = useState('');
  
  // Tab state: 'single' or 'bulk'
  const [activeTab, setActiveTab] = useState('single');

  const handleSingleSubmit = (e) => {
    e.preventDefault();
    const payload = { name: categoryName,code: categoryCode, description };
    axios.post('/api/program-categories', payload)
      .then(() => {
        setTooltip('Program category created successfully!');
        setTimeout(() => navigate('/admin/program-categories/view'), 2000);
      })
      .catch(() => {
        setTooltip('Error creating program category.');
      });
  };

  const handleBulkSubmit = (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setIsUploading(true);           // start showing progress bar
    setUploadProgress(0);  
    const formData = new FormData();
    formData.append('file', uploadFile);
    axios.post('/api/bulk-upload/program-categories', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      }
    })
      .then(() => {
        setUploadProgress(100); 
        setTooltip('Program categories uploaded successfully!');
        setTimeout(() => navigate('/admin/program-categories/view'), 2000);
      })
      .catch((err) => {
        let backendMessage = ' Error uploading program categories.';
        if (err.response && err.response.data) {
          if (typeof err.response.data === 'string') {
            backendMessage = err.response.data;
          } else if (typeof err.response.data.message === 'string') {
            backendMessage = err.response.data.message;
          }
        }
        setTooltip(`Error: ${backendMessage}`);
      })
      .finally(() => {
        setIsUploading(false); // stop showing progress bar
      });
  };

  return (
    <div className="container mt-2">
      <TitleWithBack title="Create Program Category" />
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'single' ? 'active' : ''}`}
            onClick={() => { setActiveTab('single'); setUploadFile(null); }}
          >
            Single Create
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => setActiveTab('bulk')}
          >
            Bulk Upload
          </button>
        </li>
      </ul>
      <div className="tab-content">
        {activeTab === 'single' && (
          <div className="tab-pane active">
            <div className="card shadow-sm">
              <div className="card-body">
                <form onSubmit={handleSingleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Category Name</label>
                    <input 
                      type="text"
                      className="form-control"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Code</label>
                    <input 
                      type="text"
                      className="form-control"
                      value={categoryCode}
                      onChange={(e) => setCategoryCode(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input 
                      type="text"
                      className="form-control"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">Create Category</button>
                </form>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'bulk' && (
          <div className="tab-pane active">
            <div className="card shadow-sm">
              <div className="card-body">
                <form onSubmit={handleBulkSubmit}>
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => downloadBulkUploadTemplate('programCategories')}
                    >
                      Download Template
                    </button>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Select Excel File</label>
                    <input 
                      type="file"
                      className="form-control"
                      accept=".xls,.xlsx"
                      onChange={(e) => {
                        setUploadFile(e.target.files[0]);
                        setIsUploading(false);        
                        setUploadProgress(0);         
                      }}
                    />
                  </div>
                  {uploadFile && (
                    <p className="fst-italic">File selected: {uploadFile.name}</p>
                  )}
                 {isUploading && (
                  <div className="my-3">
                   <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
                 </div>
                 )}

                  <button type="submit" className="btn btn-primary">Upload Categories</button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
    </div>
  );
}

export default ProgramCategoriesCreate;
